import { type FormEvent, useEffect, useState } from 'react'
import {
  api,
  type CureListing,
  type CureObligation,
  type CureObligationDetail,
  type CureState,
  type SourceDocument,
} from '../api'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import { ProductName } from '../components/Brand'
import { FundPicker, useFunds } from '../components/FundPicker'
import { FilterHeader, Showing, SortHeader, sortRows, useSort } from '../components/Grid'
import { RowMenu } from '../components/RowMenu'
import { DeskCell, DeskHeader, useDeskColumns, useDesks } from '../desks'

/**
 * Cure obligations — TECHNICAL_DESIGN.md §6.5, §9.5. The Snapshot Evaluator pack.
 *
 * A holding below a fund's rating floor, and the time the fund's sell-by period
 * gives to sell it, counted to the last settlement day on the fund's calendar.
 * **Those still running come first, soonest deadline first**, and one that has
 * ended stays on the list as it ended: a breach that disappeared at its deadline
 * would remove the record somebody later looks for.
 *
 * An obligation opens in place of the list, as a fund's verdicts do, with what
 * happened to it underneath. Recording the sale is the position owner's; the
 * client's consent to keep it is an approver's, and cites the letter.
 */

const STATES: CureState[] = ['escalating', 'open', 'breached', 'closed_remediated', 'closed_waived']

export const CURE_LABEL: Record<CureState, string> = {
  escalating: 'Escalating',
  open: 'Open',
  breached: 'Breached',
  closed_remediated: 'Remediated',
  closed_waived: 'Waived',
}

/** The verdict badges' colours, so a breach looks the same everywhere. */
const BADGE: Record<CureState, string> = {
  escalating: 'warn',
  open: 'not_run',
  breached: 'breach',
  closed_remediated: 'pass',
  closed_waived: 'not_evaluable',
}

const running = (o: CureObligation) => o.state === 'open' || o.state === 'escalating'

/** An obligation's two views: what it is, and what has happened to it. */
const PARTS: ['details' | 'history', string][] = [
  ['details', 'Details'],
  ['history', 'History'],
]

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function timeLeft(o: CureObligation): string {
  if (o.days_left === null) return o.closed_at ? `Ended ${o.closed_at.slice(0, 10)}` : ''
  if (o.days_left === 0) return 'Due today'
  return `${o.days_left} day${o.days_left === 1 ? '' : 's'} left`
}

const SORT_KEYS: Record<string, (o: CureObligation) => string> = {
  fund: (o) => o.portfolio_code,
  holding: (o) => o.security_id,
  event_on: (o) => o.event_on,
  deadline: (o) => o.deadline,
  state: (o) => `${STATES.indexOf(o.state)}${o.deadline}`,
  owner: (o) => o.owner ?? '',
}

function happened(action: string, after: Record<string, unknown> | null): string {
  const a = after ?? {}
  switch (action) {
    case 'cure.open':
      return a.event === 'manual' ? 'Opened by hand' : 'Opened from the fund’s holdings'
    case 'cure.escalate':
      return `Escalated to level ${String(a.level)}, ${String(a.days_left)} day(s) before the deadline`
    case 'cure.deadline':
      return `Deadline brought forward to ${String(a.deadline)}: ${String(a.because)}`
    case 'cure.remediate':
      return `Closed as remediated: ${String(a.remediation)}`
    case 'cure.waive':
      return `Closed as waived, citing ${String(a.document_title)}`
    case 'cure.owner':
      return a.owner ? `Owner changed to ${String(a.owner)}, with the fund’s` : 'Owner removed, with the fund’s'
    case 'cure.breach':
      return 'Breached: the deadline passed'
    default:
      return action
  }
}

export function CureObligations({
  focus,
  canRecord,
  canWaive,
}: {
  /** An obligation to open on arriving, as when Scheduled Jobs opens one. */
  focus?: string
  canRecord: boolean
  canWaive: boolean
}) {
  const [listing, setListing] = useState<CureListing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reads, setReads] = useState(0)
  const [fund, setFund] = useState('')
  const [only, setOnly] = useState('')
  const { sort, onSort } = useSort({ key: 'state', direction: 'asc' })
  /** The desk the list is narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const desks = useDesks()
  const deskColumns = useDeskColumns()
  const [openId, setOpenId] = useState<string | null>(focus ?? null)
  const [open, setOpen] = useState<CureObligationDetail | null>(null)
  const [acting, setActing] = useState<'remediate' | 'waive' | null>(null)
  const [opening, setOpening] = useState(false)
  const [part, setPart] = useState<'details' | 'history'>('details')

  useEffect(() => {
    if (focus) setOpenId(focus)
  }, [focus])

  // Another obligation opens on its details.
  useEffect(() => {
    setPart('details')
  }, [openId])

  // Deadlines move with the day and runs open obligations, so the page reads itself again.
  useEffect(() => {
    const timer = window.setInterval(() => setReads((n) => n + 1), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    let live = true
    api
      .cureObligations()
      .then((found) => live && setListing(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [reads])

  useEffect(() => {
    if (!openId) {
      setOpen(null)
      return
    }
    let live = true
    api
      .cureObligation(openId)
      .then((found) => live && setOpen(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [openId, reads])

  function show(id: string, then: 'remediate' | 'waive' | null = null) {
    setError(null)
    if (id !== openId) setOpen(null)
    setOpenId(id)
    setActing(then)
  }

  function changed(detail: CureObligationDetail) {
    setOpen(detail)
    setOpenId(detail.id)
    setActing(null)
    setOpening(false)
    setReads((n) => n + 1)
  }

  const errorLine = error && (
    <p className="error" role="alert">
      {error}
    </p>
  )

  // Opening one by hand is a page of its own, in place of the list, as an obligation's view is.
  if (opening && canRecord) {
    return (
      <section className="cure" data-testid="cure-open">
        <header className="page-head">
          <div className="fund-view-bar">
            <button type="button" className="ghost" onClick={() => setOpening(false)}>
              ← All obligations
            </button>
          </div>
          <h2>Open an obligation</h2>
          <p className="note">
            For an event the holdings do not show yet, such as an agency’s announcement. The deadline is counted from
            the day it happened, over the sell-by period, to the last settlement day on the fund’s calendar.
          </p>
        </header>
        <OpenObligation onDone={changed} onCancel={() => setOpening(false)} />
      </section>
    )
  }

  if (openId) {
    const o = open && open.id === openId ? open : null
    return (
      <section className="cure" data-testid="cure-detail">
        <header className="page-head">
          <div className="fund-view-bar">
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setOpenId(null)
                setActing(null)
              }}
            >
              ← All obligations
            </button>
          </div>
          {o && (
            <div className="report-title">
              <h2>
                {o.portfolio_code} · {o.security_id}
              </h2>
              <span className={`verdict-count ${BADGE[o.state]}`} data-testid="cure-state">
                {CURE_LABEL[o.state]}
              </span>
            </div>
          )}
        </header>
        {errorLine}
        {!o ? (
          !error && <p className="loading">Loading…</p>
        ) : (
          <>
            {running(o) && !acting && (canRecord || canWaive) && (
              <p className="actions">
                {canRecord && (
                  <button type="button" onClick={() => setActing('remediate')}>
                    Record remediation
                  </button>
                )}
                {canWaive && (
                  <button type="button" className="ghost" onClick={() => setActing('waive')}>
                    Record waiver
                  </button>
                )}
              </p>
            )}
            {running(o) && acting === 'remediate' && canRecord && (
              <Remediate obligation={o} onDone={changed} onCancel={() => setActing(null)} />
            )}
            {running(o) && acting === 'waive' && canWaive && (
              <Waive obligation={o} onDone={changed} onCancel={() => setActing(null)} />
            )}

            <div className="tabstrip sub" role="tablist" aria-label="Obligation">
              {PARTS.map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={part === id} onClick={() => setPart(id)}>
                  {label}
                  {id === 'history' && <span className="count">{o.history.length}</span>}
                </button>
              ))}
            </div>

            {part === 'details' && (
              <div className="table-scroll">
                <table className="grid facts-table" data-testid="cure-facts">
                  <tbody>
                    <tr>
                      <th scope="row">Fund</th>
                      <td>
                        {o.portfolio_code}
                        {o.portfolio_name !== o.portfolio_code && <span className="clause">{o.portfolio_name}</span>}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Limit</th>
                      <td>
                        {o.limit_text ?? o.rule_key}
                        <span className="clause">{o.rule_key}</span>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Holding</th>
                      <td>
                        {o.security_id}
                        {o.issuer_id && <span className="clause">{o.issuer_id}</span>}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">{o.event === 'downgrade' ? 'Fell below on' : 'Happened on'}</th>
                      <td>{o.event_on}</td>
                    </tr>
                    <tr>
                      <th scope="row">Sell-by period</th>
                      <td>{o.period_words}</td>
                    </tr>
                    <tr>
                      <th scope="row">Deadline</th>
                      <td data-testid="cure-deadline">
                        {o.deadline}
                        {running(o) && <span className="clause">{timeLeft(o)}</span>}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Counted on</th>
                      <td>{o.calendar_name ? `${o.calendar_name}: the last settlement day` : 'Calendar days'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Owner</th>
                      <td>{o.owner ?? 'Not assigned'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Opened</th>
                      <td>
                        {new Date(o.opened_at).toLocaleString()}
                        <span className="clause">
                          {o.opened_by ?? (
                            <>
                              By <ProductName />, from the fund’s holdings
                            </>
                          )}
                        </span>
                      </td>
                    </tr>
                    {o.note && (
                      <tr>
                        <th scope="row">What happened</th>
                        <td>{o.note}</td>
                      </tr>
                    )}
                    {o.remediation && (
                      <tr>
                        <th scope="row">Remediated</th>
                        <td>
                          {o.remediation}
                          {o.closed_by && <span className="clause">{o.closed_by}</span>}
                        </td>
                      </tr>
                    )}
                    {o.waiver_document_title && (
                      <tr>
                        <th scope="row">Waived</th>
                        <td>
                          {o.waiver_document_title}
                          {o.waiver_document_version && ` (${o.waiver_document_version})`}
                          {o.closed_by && <span className="clause">{o.closed_by}</span>}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {part === 'history' && (
              <div className="table-scroll">
                <table className="grid stacked" data-testid="cure-history">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>What happened</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.history.map((h, i) => (
                      <tr key={i}>
                        <td>{new Date(h.occurred_at).toLocaleString()}</td>
                        <td>{happened(h.action, h.after)}</td>
                        <td>{h.actor ?? <ProductName />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    )
  }

  const all = listing?.obligations ?? []
  const shown = sortRows(
    all.filter(
      (o) =>
        (!fund || o.portfolio_id === fund) &&
        (!only || o.state === only) &&
        (!desk || desks.fund(o.portfolio_id) === desk),
    ),
    sort,
    { ...SORT_KEYS, desk: (o) => desks.fund(o.portfolio_id) ?? '' },
  )
  const funds = [...new Map(all.map((o) => [o.portfolio_id, o.portfolio_code])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  )

  return (
    <section className="cure">
      <header className="page-head">
        <div className="report-title">
          <h2>Cure obligations</h2>
        </div>
        <p className="note">
          Holdings that have fallen below a fund’s rating floor, and the time each has left to be sold,
          counted to the last settlement day on the fund’s calendar. Part of the <EvaluatorName strong={false} />.
          <AddOnFootnote />
        </p>
      </header>

      {canRecord && (
        <p className="actions">
          <button type="button" className="ghost" onClick={() => setOpening(true)}>
            Open an obligation
          </button>
        </p>
      )}

      {errorLine}

      {listing === null ? (
        !error && <p className="loading">Loading…</p>
      ) : (
        <>
          <div className="verdict-tiles" data-testid="cure-totals">
            {STATES.map((s) => (
              <button
                key={s}
                type="button"
                className={`verdict-tile ${BADGE[s]}${only === s ? ' on' : ''}`}
                aria-pressed={only === s}
                data-testid={`cure-total-${s}`}
                onClick={() => setOnly(only === s ? '' : s)}
              >
                <strong>{listing.totals[s]}</strong>
                <span>{CURE_LABEL[s]}</span>
              </button>
            ))}
          </div>

          {all.length === 0 ? (
            <p className="empty" data-testid="cure-empty">
              No cure obligations. One opens when a fund’s newest holdings show a bond fallen below its rating
              floor within the fund’s sell-by period.
            </p>
          ) : (
            <div className="table-scroll">
              <Showing
                shown={shown.length}
                total={all.length}
                noun="obligations"
                onClear={() => {
                  setFund('')
                  setOnly('')
                  setDesk('')
                }}
              />
              <table className="grid stacked" data-testid="cure-list">
                <thead>
                  <tr>
                    <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
                    <FilterHeader
                      label="Fund"
                      sortKey="fund"
                      sort={sort}
                      onSort={onSort}
                      value={fund}
                      onChange={setFund}
                      all="Every fund"
                      testId="cure-filter-fund"
                      options={funds.map(([value, label]) => ({ value, label }))}
                    />
                    <SortHeader label="Holding" sortKey="holding" sort={sort} onSort={onSort} />
                    <th>Limit</th>
                    <SortHeader label="Fell below" sortKey="event_on" sort={sort} onSort={onSort} />
                    <SortHeader label="Deadline" sortKey="deadline" sort={sort} onSort={onSort} />
                    <FilterHeader
                      label="State"
                      sortKey="state"
                      sort={sort}
                      onSort={onSort}
                      value={only}
                      onChange={setOnly}
                      all="Every state"
                      testId="cure-filter-state"
                      options={STATES.map((s) => ({ value: s, label: CURE_LABEL[s] }))}
                    />
                    <SortHeader label="Owner" sortKey="owner" sort={sort} onSort={onSort} />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={8 + deskColumns} className="empty">
                        No obligation matches these filters.
                      </td>
                    </tr>
                  )}
                  {shown.map((o) => (
                    <tr
                      key={o.id}
                      data-testid="cure-row"
                      className="clickable"
                      title={`Open ${o.portfolio_code} ${o.security_id}`}
                      onClick={() => show(o.id)}
                    >
                      <DeskCell fund={o.portfolio_id} />
                      <td>
                        <strong>{o.portfolio_code}</strong>
                        {o.portfolio_name !== o.portfolio_code && <span className="clause">{o.portfolio_name}</span>}
                      </td>
                      <td>
                        {o.security_id}
                        {o.issuer_id && <span className="clause">{o.issuer_id}</span>}
                      </td>
                      <td>
                        {o.limit_text ?? o.rule_key}
                        <span className="clause">{o.period_words} sell-by period</span>
                      </td>
                      <td>
                        {o.event_on}
                        {o.event === 'manual' && <span className="clause">Opened by hand</span>}
                      </td>
                      <td>
                        {o.deadline}
                        <span className={`clause${o.state === 'escalating' ? ' check-differs' : ''}`}>{timeLeft(o)}</span>
                      </td>
                      <td>
                        <span className={`verdict-count ${BADGE[o.state]}`}>{CURE_LABEL[o.state]}</span>
                      </td>
                      <td>{o.owner ?? '—'}</td>
                      <td className="run-actions" onClick={(e) => e.stopPropagation()}>
                        <RowMenu
                          label={`Actions for ${o.portfolio_code} ${o.security_id}`}
                          testId="cure-menu"
                          items={[
                            { label: 'Details', onSelect: () => show(o.id) },
                            ...(canRecord
                              ? [
                                  {
                                    label: 'Record remediation',
                                    disabled: !running(o),
                                    why: running(o) ? undefined : 'It has ended.',
                                    onSelect: () => show(o.id, 'remediate'),
                                  },
                                ]
                              : []),
                            ...(canWaive
                              ? [
                                  {
                                    label: 'Record waiver',
                                    disabled: !running(o),
                                    why: running(o) ? undefined : 'It has ended.',
                                    onSelect: () => show(o.id, 'waive'),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Remediate({
  obligation,
  onDone,
  onCancel,
}: {
  obligation: CureObligation
  onDone: (detail: CureObligationDetail) => void
  onCancel: () => void
}) {
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onDone(await api.remediateCureObligation(obligation.id, reference, note.trim() || null))
    } catch (x) {
      setError(said(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card cure-form" data-testid="remediate-form" onSubmit={(e) => void submit(e)}>
      <h3>Record remediation</h3>
      <label>
        What cured it
        <input
          value={reference}
          placeholder="The sale’s trade reference"
          onChange={(e) => setReference(e.target.value)}
        />
      </label>
      <label>
        Note
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="actions">
        <button type="submit" disabled={busy || !reference.trim()}>
          Close as remediated
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </p>
    </form>
  )
}

function Waive({
  obligation,
  onDone,
  onCancel,
}: {
  obligation: CureObligation
  onDone: (detail: CureObligationDetail) => void
  onCancel: () => void
}) {
  const [documents, setDocuments] = useState<SourceDocument[] | null>(null)
  const [documentId, setDocumentId] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api
      .documents()
      .then((found) => live && setDocuments(found.filter((d) => d.status !== 'unresolved')))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onDone(await api.waiveCureObligation(obligation.id, documentId, note.trim() || null))
    } catch (x) {
      setError(said(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card cure-form" data-testid="waive-form" onSubmit={(e) => void submit(e)}>
      <h3>Record waiver</h3>
      <p className="note">
        The client agreed to {obligation.security_id} being kept. Cite their letter, registered under Mandate
        documents.
      </p>
      <label>
        The client’s consent
        <select value={documentId} onChange={(e) => setDocumentId(e.target.value)} data-testid="waive-document">
          <option value="">{documents?.length === 0 ? 'Register the letter under Mandate documents first' : 'Choose a document'}</option>
          {(documents ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.title}
              {d.version_label ? ` (${d.version_label})` : ''}
            </option>
          ))}
        </select>
      </label>
      <label>
        Note
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="actions">
        <button type="submit" disabled={busy || !documentId}>
          Close as waived
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </p>
    </form>
  )
}

function OpenObligation({
  onDone,
  onCancel,
}: {
  onDone: (detail: CureObligationDetail) => void
  onCancel: () => void
}) {
  const { funds } = useFunds()
  const today = new Date().toISOString().slice(0, 10)
  const [fund, setFund] = useState('')
  const [limits, setLimits] = useState<{ id: string; label: string }[]>([])
  /** The chosen fund's own sell-by period, where its limits state one. */
  const [stated, setStated] = useState<string | null>(null)
  const [ruleId, setRuleId] = useState('')
  const [security, setSecurity] = useState('')
  const [issuer, setIssuer] = useState('')
  const [eventOn, setEventOn] = useState(today)
  const [period, setPeriod] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setRuleId('')
    setStated(null)
    if (!fund) {
      setLimits([])
      return
    }
    let live = true
    api
      .guidelines(fund)
      .then((found) => {
        if (!live) return
        const seen = new Map<string, string>()
        for (const c of found.constraints) {
          for (const r of [c.binding, ...c.also]) {
            if (!r) continue
            if (r.template === 'evaluation_policy' && r.subject?.selector === 'downgrade_grace') {
              setStated(periodWords(String(r.threshold)))
            } else if (r.template !== 'evaluation_policy' && r.template !== 'cure_obligation') {
              seen.set(r.id, r.rule_key)
            }
          }
        }
        setLimits([...seen].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label)))
      })
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [fund])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onDone(
        await api.openCureObligation({
          portfolio_id: fund,
          rule_id: ruleId,
          security_id: security,
          issuer_id: issuer.trim() || null,
          event_on: eventOn,
          period: period.trim() || null,
          note,
        }),
      )
    } catch (x) {
      setError(said(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="obligation-form" data-testid="open-obligation" onSubmit={(e) => void submit(e)}>
      <fieldset>
        <legend>Fund and limit</legend>
        <FundPicker label="Fund" value={fund} onChange={setFund} funds={funds} testId="open-fund" />
        <label>
          Limit
          <select value={ruleId} onChange={(e) => setRuleId(e.target.value)} data-testid="open-limit">
            <option value="">{fund ? 'Choose a limit' : 'Choose a fund first'}</option>
            {limits.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      </fieldset>

      <fieldset>
        <legend>The holding</legend>
        <label>
          Security identifier
          <input
            value={security}
            onChange={(e) => setSecurity(e.target.value)}
            placeholder="As the fund’s snapshots name it"
          />
        </label>
        <label>
          Issuer
          <input value={issuer} onChange={(e) => setIssuer(e.target.value)} />
        </label>
      </fieldset>

      <fieldset>
        <legend>What happened</legend>
        <label>
          Happened on
          <input type="date" max={today} value={eventOn} onChange={(e) => setEventOn(e.target.value)} />
        </label>
        <label className="wide">
          Say what happened
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="The agency, the new rating and the day it was announced"
          />
        </label>
      </fieldset>

      <fieldset>
        <legend>Sell-by period</legend>
        <label>
          Period
          <input
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder={stated ? `The fund’s own, ${stated}` : 'P30D, P6W, P6M or P1Y'}
          />
          <span className="clause" data-testid="open-period-hint">
            {!fund
              ? 'Choose a fund to see its own sell-by period.'
              : stated
                ? `Leave empty for the fund’s own, ${stated}, or give the period the client allows.`
                : 'Leave empty for the fund’s own sell-by period, or give the period the client allows.'}
          </span>
        </label>
      </fieldset>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <p className="actions">
        <button type="submit" disabled={busy || !fund || !ruleId || !security.trim() || !note.trim()}>
          Open
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </p>
    </form>
  )
}

/** `P6M` as *6 months*. */
function periodWords(period: string): string | null {
  const match = /^P(\d{1,4})([DWMY])$/.exec(period)
  if (!match) return null
  const count = Number(match[1])
  const unit = ({ D: 'day', W: 'week', M: 'month', Y: 'year' } as Record<string, string>)[match[2] ?? '']
  return `${count} ${unit}${count === 1 ? '' : 's'}`
}
